import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Switch,
  Drawer,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  OutlinedInput,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Delete,
  Visibility,
  Edit,
  PlayArrow,
  Close,
} from '@mui/icons-material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import { buildEntityWhere } from '../../utils/entity-list';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import {
  rulesService,
  Rule,
  RuleScopeType,
  RuleMetric,
  RuleOperator,
  RuleTimeframe,
  TimeframeWindow,
  ConditionGroup,
  ConditionLeaf,
  RuleAction,
  RuleEvaluation,
  RuleTestResult,
  CreateRuleData,
  isConditionGroup,
} from '../../services/rules.service';
import { alertChannelsService, AlertChannel } from '../../services/alert-channels.service';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const METRICS: RuleMetric[] = [
  'visits', 'unique_visits', 'clicks', 'unique_clicks', 'conversions',
  'revenue', 'cost', 'profit', 'roi', 'roas', 'epv', 'cpv', 'cr', 'ctr', 'cv_rate', 'rpl', 'cpl',
];

const OPERATORS: RuleOperator[] = ['>', '>=', '<', '<=', '==', '!='];

const SCOPE_TYPES: { value: RuleScopeType; label: string }[] = [
  { value: 'traffic_source', label: 'Traffic Source' },
  { value: 'flow', label: 'Flow' },
  { value: 'offer', label: 'Offer' },
  { value: 'network', label: 'Network' },
  { value: 'landing_page', label: 'Landing Page' },
  { value: 'dimension', label: 'Dimension' },
  { value: 'global', label: 'Global' },
];

const SCOPE_LABELS: Record<RuleScopeType, string> = SCOPE_TYPES.reduce(
  (acc, s) => ({ ...acc, [s.value]: s.label }),
  {} as Record<RuleScopeType, string>
);

const DIMENSIONS = [
  'v1', 'v2', 'v3', 'v4', 'v5', 'v6', 'v7', 'v8', 'v9', 'v10',
  'country', 'device', 'os', 'region', 'language', 'referrer_domain',
];

const TIMEFRAME_WINDOWS: { value: TimeframeWindow; label: string }[] = [
  { value: 'last_hours', label: 'Last N hours' },
  { value: 'last_days', label: 'Last N days' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function countLeaves(group: ConditionGroup): number {
  return group.conditions.reduce((sum, node) => {
    return sum + (isConditionGroup(node) ? countLeaves(node) : 1);
  }, 0);
}

function summarizeLeaf(leaf: ConditionLeaf): string {
  if (leaf.type === 'threshold') return `${leaf.metric} ${leaf.operator} ${leaf.value}`;
  if (leaf.type === 'comparison') return `${leaf.metric} ${leaf.operator} ${leaf.rhs_metric}`;
  return `${leaf.metric} ${leaf.operator} prev${leaf.factor != null ? ` x${leaf.factor}` : ''}`;
}

function summarizeGroup(group: ConditionGroup): string {
  const parts = group.conditions.map((node) =>
    isConditionGroup(node) ? `(${summarizeGroup(node)})` : summarizeLeaf(node)
  );
  return parts.join(` ${group.op} `);
}

function newLeaf(type: ConditionLeaf['type']): ConditionLeaf {
  if (type === 'comparison') {
    return { type: 'comparison', metric: 'revenue', operator: '>', rhs_metric: 'cost' };
  }
  if (type === 'period') {
    return { type: 'period', metric: 'revenue', operator: '<', compare_to: 'previous', factor: 1 };
  }
  return { type: 'threshold', metric: 'visits', operator: '>', value: 0 };
}

function newGroup(): ConditionGroup {
  return { op: 'AND', conditions: [newLeaf('threshold')] };
}

// ---------------------------------------------------------------------------
// Form types
// ---------------------------------------------------------------------------

interface RuleFormData {
  name: string;
  workspace_id: string;
  enabled: boolean;
  scope_type: RuleScopeType;
  dimension: string | null;
  filtersText: string;
  timeframe: RuleTimeframe;
  exclude_hours: number;
  frequency_minutes: number;
  cooldown_hours: number;
  conditions: ConditionGroup;
  channelIds: string[];
}

function emptyForm(): RuleFormData {
  return {
    name: '',
    workspace_id: defaultCreateWorkspace(),
    enabled: true,
    scope_type: 'global',
    dimension: null,
    filtersText: '{}',
    timeframe: { window: 'last_hours', value: 24 },
    exclude_hours: 0,
    frequency_minutes: 60,
    cooldown_hours: 24,
    conditions: newGroup(),
    channelIds: [],
  };
}

function ruleToForm(rule: Rule): RuleFormData {
  return {
    name: rule.name,
    workspace_id: (rule as any).workspace_id ?? defaultCreateWorkspace(),
    enabled: rule.enabled,
    scope_type: rule.scope_type,
    dimension: rule.dimension,
    filtersText: JSON.stringify(rule.filters ?? {}, null, 2),
    timeframe: rule.timeframe ?? { window: 'last_hours', value: 24 },
    exclude_hours: rule.exclude_hours ?? 0,
    frequency_minutes: rule.frequency_minutes ?? 60,
    cooldown_hours: rule.cooldown_hours ?? 24,
    conditions: rule.conditions ?? newGroup(),
    channelIds: (rule.actions ?? []).map((a) => a.channel_id),
  };
}

// ---------------------------------------------------------------------------
// Leaf editor
// ---------------------------------------------------------------------------

function LeafEditor({
  leaf,
  onChange,
  onRemove,
  canRemove,
}: {
  leaf: ConditionLeaf;
  onChange: (leaf: ConditionLeaf) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const changeType = (type: ConditionLeaf['type']) => {
    const base = newLeaf(type);
    // preserve metric + operator where possible
    onChange({ ...base, metric: leaf.metric, operator: leaf.operator } as ConditionLeaf);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select value={leaf.type} onChange={(e) => changeType(e.target.value as ConditionLeaf['type'])}>
          <MenuItem value="threshold">Threshold</MenuItem>
          <MenuItem value="comparison">Comparison</MenuItem>
          <MenuItem value="period">Period</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <Select
          value={leaf.metric}
          onChange={(e) => onChange({ ...leaf, metric: e.target.value as RuleMetric })}
        >
          {METRICS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 70 }}>
        <Select
          value={leaf.operator}
          onChange={(e) => onChange({ ...leaf, operator: e.target.value as RuleOperator })}
        >
          {OPERATORS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </Select>
      </FormControl>

      {leaf.type === 'threshold' && (
        <TextField
          size="small"
          type="number"
          value={leaf.value}
          onChange={(e) => onChange({ ...leaf, value: parseFloat(e.target.value) || 0 })}
          sx={{ width: 100 }}
        />
      )}

      {leaf.type === 'comparison' && (
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={leaf.rhs_metric}
            onChange={(e) => onChange({ ...leaf, rhs_metric: e.target.value as RuleMetric })}
          >
            {METRICS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {leaf.type === 'period' && (
        <>
          <Typography variant="body2" color="text.secondary">prev x</Typography>
          <TextField
            size="small"
            type="number"
            value={leaf.factor ?? 1}
            onChange={(e) => onChange({ ...leaf, factor: parseFloat(e.target.value) || 0 })}
            sx={{ width: 90 }}
          />
        </>
      )}

      <IconButton size="small" onClick={onRemove} disabled={!canRemove}>
        <Delete fontSize="small" />
      </IconButton>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Recursive group editor
// ---------------------------------------------------------------------------

function GroupEditor({
  group,
  onChange,
  onRemove,
  depth,
}: {
  group: ConditionGroup;
  onChange: (group: ConditionGroup) => void;
  onRemove?: () => void;
  depth: number;
}) {
  const setOp = (op: 'AND' | 'OR') => onChange({ ...group, op });

  const updateChild = (index: number, node: ConditionLeaf | ConditionGroup) => {
    const next = [...group.conditions];
    next[index] = node;
    onChange({ ...group, conditions: next });
  };

  const removeChild = (index: number) => {
    onChange({ ...group, conditions: group.conditions.filter((_, i) => i !== index) });
  };

  const addLeaf = () => onChange({ ...group, conditions: [...group.conditions, newLeaf('threshold')] });
  const addGroup = () => onChange({ ...group, conditions: [...group.conditions, newGroup()] });

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, borderColor: depth > 0 ? 'primary.light' : 'divider', bgcolor: depth > 0 ? 'action.hover' : 'transparent' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={group.op}
          onChange={(_, v) => { if (v) setOp(v); }}
        >
          <ToggleButton value="AND">AND</ToggleButton>
          <ToggleButton value="OR">OR</ToggleButton>
        </ToggleButtonGroup>
        {onRemove && (
          <IconButton size="small" onClick={onRemove}>
            <Delete fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {group.conditions.map((node, i) =>
          isConditionGroup(node) ? (
            <GroupEditor
              key={i}
              group={node}
              depth={depth + 1}
              onChange={(g) => updateChild(i, g)}
              onRemove={() => removeChild(i)}
            />
          ) : (
            <LeafEditor
              key={i}
              leaf={node}
              onChange={(l) => updateChild(i, l)}
              onRemove={() => removeChild(i)}
              canRemove={group.conditions.length > 1 || depth > 0}
            />
          )
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button size="small" startIcon={<Add />} onClick={addLeaf}>Condition</Button>
        {depth < 2 && (
          <Button size="small" startIcon={<Add />} onClick={addGroup}>Group</Button>
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function RulesPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [form, setForm] = useState<RuleFormData>(emptyForm());
  const [drawerTab, setDrawerTab] = useState(0);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState<RuleTestResult | null>(null);

  // Queries
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);
  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('rule', { search: debouncedSearch, statuses, tagIds: [] }, ['name']),
    [debouncedSearch, statuses],
  );
  const { data: rulesResult, isFetching } = useQuery({
    queryKey: ['rules', where],
    queryFn: () => rulesService.query({ where }),
  });
  const rules: Rule[] = useMemo(() => rulesResult?.data || [], [rulesResult]);

  const { data: channelsData } = useQuery({
    queryKey: ['alert-channels-for-rules'],
    queryFn: () => alertChannelsService.query({ limit: 200 }),
  });
  const channels: AlertChannel[] = (channelsData?.data || []).filter((c) => c.enabled);
  const channelById = new Map(channels.map((c) => [c.id, c]));

  const { data: evaluations = [] } = useQuery({
    queryKey: ['rule-evaluations', editingRuleId],
    queryFn: () => rulesService.getEvaluations(editingRuleId!, { limit: 50 }),
    enabled: !!editingRuleId && drawerTab === 1,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateRuleData) => rulesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      enqueueSnackbar('Rule created', { variant: 'success' });
      closeDrawer();
    },
    onError: (err: Error) => enqueueSnackbar(`Failed to create rule: ${err.message}`, { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateRuleData> }) =>
      rulesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      enqueueSnackbar('Rule updated', { variant: 'success' });
      closeDrawer();
    },
    onError: (err: Error) => enqueueSnackbar(`Failed to update rule: ${err.message}`, { variant: 'error' }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: rulesService.unarchive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      enqueueSnackbar('Rule restored', { variant: 'success' });
    },
    onError: (err: Error) => enqueueSnackbar(`Failed to restore rule: ${err.message}`, { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: rulesService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      enqueueSnackbar('Rule archived', { variant: 'success' });
    },
    onError: (err: Error) => enqueueSnackbar(`Failed to archive rule: ${err.message}`, { variant: 'error' }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      rulesService.update(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rules'] }),
    onError: (err: Error) => enqueueSnackbar(`Failed to update rule: ${err.message}`, { variant: 'error' }),
  });

  const testMutation = useMutation({
    mutationFn: rulesService.test,
    onSuccess: (result) => {
      setTestResult(result);
      setTestModalOpen(true);
    },
    onError: (err: Error) => enqueueSnackbar(`Test failed: ${err.message}`, { variant: 'error' }),
  });

  // Handlers
  const openNewRule = () => {
    setEditingRuleId(null);
    setForm(emptyForm());
    setDrawerTab(0);
    setDrawerOpen(true);
  };

  const openEditRule = (rule: Rule) => {
    setEditingRuleId(rule.id);
    setForm(ruleToForm(rule));
    setDrawerTab(0);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRuleId(null);
  };

  const buildPayload = (): CreateRuleData | null => {
    let filters: Record<string, unknown> = {};
    try {
      filters = form.filtersText.trim() ? JSON.parse(form.filtersText) : {};
    } catch {
      enqueueSnackbar('Filters is not valid JSON', { variant: 'error' });
      return null;
    }
    const actions: RuleAction[] = form.channelIds.map((channel_id) => ({ type: 'notify', channel_id }));
    return {
      name: form.name,
      enabled: form.enabled,
      scope_type: form.scope_type,
      dimension: form.scope_type === 'dimension' ? form.dimension : null,
      filters,
      timeframe: form.timeframe,
      exclude_hours: form.exclude_hours,
      frequency_minutes: form.frequency_minutes,
      cooldown_hours: form.cooldown_hours,
      conditions: form.conditions,
      actions,
    };
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (!payload) return;
    if (editingRuleId) {
      updateMutation.mutate({ id: editingRuleId, data: payload });
    } else {
      createMutation.mutate({ ...payload, workspace_id: form.workspace_id } as any);
    }
  };

  const timeframeNeedsValue =
    form.timeframe.window === 'last_hours' || form.timeframe.window === 'last_days';

  // DataGrid columns
  const columns: GridColDef<Rule>[] = [
    { field: 'name', headerName: 'Rule Name', flex: 1, minWidth: 180 },
    {
      field: 'scope_type',
      headerName: 'Scope',
      width: 150,
      renderCell: (params: GridRenderCellParams<Rule>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={
              params.row.scope_type === 'dimension' && params.row.dimension
                ? `Dimension: ${params.row.dimension}`
                : SCOPE_LABELS[params.row.scope_type] || params.row.scope_type
            }
            size="small"
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 90,
      renderCell: (params: GridRenderCellParams<Rule>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Switch
            checked={params.value}
            size="small"
            disabled={toggleMutation.isPending}
            onChange={(e) => {
              e.stopPropagation();
              toggleMutation.mutate({ id: params.row.id, enabled: !params.value });
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      ),
    },
    {
      field: 'conditions',
      headerName: 'Conditions',
      flex: 1,
      minWidth: 200,
      sortable: false,
      valueGetter: (_value: unknown, row: Rule) =>
        row.conditions ? summarizeGroup(row.conditions) : '',
    },
    {
      field: 'actions_count',
      headerName: 'Channels',
      width: 100,
      sortable: false,
      valueGetter: (_value: unknown, row: Rule) => (row.actions?.length ?? 0),
    },
    {
      field: 'last_evaluated_at',
      headerName: 'Last Evaluated',
      width: 130,
      valueGetter: (_value: unknown, row: Rule) => timeAgo(row.last_evaluated_at),
    },
    {
      field: 'actions',
      headerName: '',
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Rule>) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
          <Tooltip title="Test rule">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                testMutation.mutate(params.row.id);
              }}
              disabled={testMutation.isPending}
            >
              <PlayArrow fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openEditRule(params.row);
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'archived' ? (
            <Tooltip title="Restore">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); unarchiveMutation.mutate(params.row.id); }}
              >
                <Visibility fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Archive">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('Archive this rule? Its evaluation history is kept.')) {
                    deleteMutation.mutate(params.row.id);
                  }
                }}
              >
                <Delete fontSize="small" color="error" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const canSave = !!form.name.trim() && form.channelIds.length > 0 && countLeaves(form.conditions) > 0
    && (form.scope_type !== 'dimension' || !!form.dimension) && !!form.workspace_id;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Rules</Typography>
      </Box>

      {/* Rules list */}
      <DataGridPremium
        rows={rules}
        columns={columns}
        loading={isFetching}
        autoHeight
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          statuses: { value: statuses, onChange: setStatuses },
          actions: [{ icon: Add, hint: 'New rule', onClick: openNewRule }],
        } }}
        disableRowSelectionOnClick
        onRowClick={(params: { row: Rule }) => openEditRule(params.row)}
        initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
        sx={{
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 1 },
          bgcolor: 'background.paper',
          borderRadius: 2,
        }}
      />

      {/* Edit Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 620 } } }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{editingRuleId ? 'Edit Rule' : 'New Rule'}</Typography>
            <IconButton onClick={closeDrawer}><Close /></IconButton>
          </Box>

          <Tabs value={drawerTab} onChange={(_, v) => setDrawerTab(v)} sx={{ mb: 2 }}>
            <Tab label="Configuration" />
            {editingRuleId && <Tab label="Evaluations" />}
          </Tabs>

          {drawerTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Rule Name"
                fullWidth
                size="small"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
              {!editingRuleId && (
                <WorkspaceField
                  value={form.workspace_id}
                  onChange={(id) => setForm((p) => ({ ...p, workspace_id: id }))}
                />
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                />
                <Typography variant="body2">Enabled</Typography>
              </Box>

              <Divider textAlign="left"><Typography variant="caption">Scope</Typography></Divider>

              <FormControl fullWidth size="small">
                <InputLabel>Scope Type</InputLabel>
                <Select
                  value={form.scope_type}
                  label="Scope Type"
                  onChange={(e: SelectChangeEvent) =>
                    setForm((p) => ({ ...p, scope_type: e.target.value as RuleScopeType }))
                  }
                >
                  {SCOPE_TYPES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {form.scope_type === 'dimension' && (
                <FormControl fullWidth size="small">
                  <InputLabel>Dimension</InputLabel>
                  <Select
                    value={form.dimension || ''}
                    label="Dimension"
                    onChange={(e: SelectChangeEvent) =>
                      setForm((p) => ({ ...p, dimension: e.target.value || null }))
                    }
                  >
                    {DIMENSIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              )}

              {form.scope_type !== 'global' && (
                <TextField
                  label="Filters (JSON)"
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  value={form.filtersText}
                  onChange={(e) => setForm((p) => ({ ...p, filtersText: e.target.value }))}
                  helperText='Optional entity/value filters, e.g. {"offer_id": ["123"], "country": "US"}'
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
                />
              )}

              <Divider textAlign="left"><Typography variant="caption">Timeframe & Schedule</Typography></Divider>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Window</InputLabel>
                  <Select
                    value={form.timeframe.window}
                    label="Window"
                    onChange={(e: SelectChangeEvent) =>
                      setForm((p) => ({
                        ...p,
                        timeframe: { ...p.timeframe, window: e.target.value as TimeframeWindow },
                      }))
                    }
                  >
                    {TIMEFRAME_WINDOWS.map((w) => (
                      <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {timeframeNeedsValue && (
                  <TextField
                    label="Value"
                    type="number"
                    size="small"
                    sx={{ width: 110 }}
                    value={form.timeframe.value ?? 0}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        timeframe: { ...p.timeframe, value: parseInt(e.target.value) || 0 },
                      }))
                    }
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Exclude hours"
                  type="number"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 0, max: 168 }}
                  value={form.exclude_hours}
                  onChange={(e) => setForm((p) => ({ ...p, exclude_hours: parseInt(e.target.value) || 0 }))}
                />
                <TextField
                  label="Frequency (min)"
                  type="number"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 5, max: 1440 }}
                  value={form.frequency_minutes}
                  onChange={(e) => setForm((p) => ({ ...p, frequency_minutes: parseInt(e.target.value) || 5 }))}
                />
                <TextField
                  label="Cooldown (hrs)"
                  type="number"
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ min: 0, max: 720 }}
                  value={form.cooldown_hours}
                  onChange={(e) => setForm((p) => ({ ...p, cooldown_hours: parseInt(e.target.value) || 0 }))}
                />
              </Box>

              <Divider textAlign="left"><Typography variant="caption">Conditions</Typography></Divider>

              <GroupEditor
                group={form.conditions}
                depth={0}
                onChange={(g) => setForm((p) => ({ ...p, conditions: g }))}
              />

              <Divider textAlign="left"><Typography variant="caption">Actions</Typography></Divider>

              <FormControl fullWidth size="small">
                <InputLabel>Notify Channels</InputLabel>
                <Select
                  multiple
                  value={form.channelIds}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((p) => ({
                      ...p,
                      channelIds: typeof value === 'string' ? value.split(',') : value,
                    }));
                  }}
                  input={<OutlinedInput label="Notify Channels" />}
                  renderValue={(selected) =>
                    selected.map((id) => channelById.get(id)?.name || id).join(', ')
                  }
                >
                  {channels.length === 0 && (
                    <MenuItem disabled value="">No enabled alert channels</MenuItem>
                  )}
                  {channels.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={form.channelIds.indexOf(c.id) > -1} />
                      <ListItemText primary={c.name} secondary={c.type} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving || !canSave}
                >
                  {isSaving ? <CircularProgress size={20} /> : editingRuleId ? 'Save' : 'Create'}
                </Button>
                {editingRuleId && (
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrow />}
                    onClick={() => testMutation.mutate(editingRuleId)}
                    disabled={testMutation.isPending}
                  >
                    Test
                  </Button>
                )}
                <Button variant="text" onClick={closeDrawer}>Cancel</Button>
              </Box>
            </Box>
          )}

          {/* Evaluations tab */}
          {drawerTab === 1 && editingRuleId && (
            <Box>
              {evaluations.length === 0 ? (
                <Typography color="text.secondary" sx={{ mt: 2 }}>No evaluations yet.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Matched</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evaluations.map((ev: RuleEvaluation) => (
                      <TableRow key={ev.id}>
                        <TableCell>{ev.entity_label || ev.entity_id}</TableCell>
                        <TableCell>
                          <Chip
                            label={ev.matched ? 'Yes' : 'No'}
                            size="small"
                            color={ev.matched ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={ev.action_taken || '-'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{timeAgo(ev.evaluated_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Test Results Modal */}
      <Dialog open={testModalOpen} onClose={() => setTestModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Test Results</DialogTitle>
        <DialogContent>
          {testResult && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {testResult.matched} matching entit{testResult.matched === 1 ? 'y' : 'ies'}.
              </Typography>
              {testResult.entities.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Metrics</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResult.entities.map((ent) => (
                      <TableRow key={ent.entity_id}>
                        <TableCell>{ent.entity_label || ent.entity_id}</TableCell>
                        <TableCell>
                          {Object.entries(ent.metrics || {})
                            .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toFixed(2) : v}`)
                            .join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">No entities matched the rule conditions.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
